<!-- components/profile/EditProfileModal.vue -->
<template>
  <Teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Edit Profile</h3>
          <button class="close-btn" @click="$emit('close')">×</button>
        </div>

        <form @submit.prevent="saveProfile" class="edit-form">
          <div class="form-group">
            <label>Display Name</label>
            <input
              v-model="form.displayName"
              type="text"
              placeholder="Your name"
              required
            />
          </div>

          <div class="form-group">
            <label>Bio</label>
            <textarea
              v-model="form.bio"
              rows="3"
              placeholder="Tell people about yourself..."
              maxlength="500"
            ></textarea>
            <span class="char-count">{{ form.bio?.length || 0 }}/500</span>
          </div>

          <div class="form-group">
            <label>Location</label>
            <input
              v-model="form.location"
              type="text"
              placeholder="City, Country"
            />
          </div>

          <div class="form-group">
            <label>Role</label>
            <select v-model="form.role">
              <option value="learner">Learner - I want to learn</option>
              <option value="creator">Creator - I want to teach</option>
            </select>
          </div>

          <div class="form-group">
            <label>Skills (comma separated)</label>
            <input
              v-model="skillsInput"
              type="text"
              placeholder="JavaScript, Python, UI/UX..."
            />
            <small class="hint">Separate skills with commas</small>
          </div>

          <div class="form-group">
            <label>Interests (comma separated)</label>
            <input
              v-model="interestsInput"
              type="text"
              placeholder="AI, Design, Business..."
            />
            <small class="hint">Separate interests with commas</small>
          </div>

          <div class="form-group">
            <label>Social Links</label>
            <div class="social-inputs">
              <div
                v-for="(url, platform) in form.socialLinks"
                :key="platform"
                class="social-input-row"
              >
                <span class="social-icon">{{ getSocialIcon(platform) }}</span>
                <input
                  v-model="form.socialLinks[platform]"
                  type="url"
                  :placeholder="`${platform} URL`"
                />
                <button
                  type="button"
                  class="remove-social"
                  @click="removeSocial(platform)"
                >
                  ×
                </button>
              </div>
            </div>
            <div class="add-social">
              <select v-model="newSocialPlatform">
                <option value="">Add social link...</option>
                <option value="github">GitHub</option>
                <option value="website">Website</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter/X</option>
                <option value="youtube">YouTube</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option>
              </select>
              <button
                type="button"
                class="add-btn"
                @click="addSocial"
                :disabled="!newSocialPlatform"
              >
                Add
              </button>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('close')">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : "Save Changes" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close", "updated"]);

const authStore = useAuthStore();
const userStore = useUserStore();

const saving = ref(false);
const newSocialPlatform = ref("");
const skillsInput = ref("");
const interestsInput = ref("");

const form = reactive({
  displayName: "",
  bio: "",
  location: "",
  role: "learner",
  skills: [],
  interests: [],
  socialLinks: {},
});

// Initialize form with user data
watch(
  () => props.user,
  (user) => {
    if (user) {
      form.displayName = user.displayName || "";
      form.bio = user.bio || "";
      form.location = user.location || "";
      form.role = user.role || "learner";
      form.skills = user.skills || [];
      form.interests = user.interests || [];
      form.socialLinks = { ...(user.socialLinks || {}) };

      skillsInput.value = (user.skills || []).join(", ");
      interestsInput.value = (user.interests || []).join(", ");
    }
  },
  { immediate: true },
);

const getSocialIcon = (platform) => {
  const icons = {
    github: "🐙",
    website: "🌐",
    linkedin: "💼",
    twitter: "🐦",
    youtube: "▶️",
    instagram: "📸",
    facebook: "📘",
    tiktok: "🎵",
  };
  return icons[platform] || "🔗";
};

const addSocial = () => {
  if (newSocialPlatform.value && !form.socialLinks[newSocialPlatform.value]) {
    form.socialLinks[newSocialPlatform.value] = "";
    newSocialPlatform.value = "";
  }
};

const removeSocial = (platform) => {
  delete form.socialLinks[platform];
  // Recreate reactive object
  form.socialLinks = { ...form.socialLinks };
};

const saveProfile = async () => {
  saving.value = true;

  // Parse skills and interests from comma-separated strings
  const skills = skillsInput.value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s);

  const interests = interestsInput.value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s);

  const profileData = {
    displayName: form.displayName,
    bio: form.bio,
    location: form.location,
    role: form.role,
    skills,
    interests,
    socialLinks: form.socialLinks,
  };

  try {
    const result = await authStore.updateUserProfile(profileData);
    if (result.success) {
      emit("updated");
      emit("close");
    }
  } catch (error) {
    console.error("Profile update error:", error);
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    color: #1a1a1a;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    color: #868e96;
    cursor: pointer;

    &:hover {
      color: #1a1a1a;
    }
  }
}

.edit-form {
  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 6px;
      font-size: 14px;
    }

    input,
    textarea,
    select {
      width: 100%;
      padding: 10px 12px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 14px;
      transition: all 0.3s ease;

      &:focus {
        border-color: $primary-orange;
        outline: none;
        box-shadow: 0 0 0 4px rgba(255, 128, 0, 0.1);
      }
    }

    textarea {
      resize: vertical;
      font-family: inherit;
    }

    .char-count {
      display: block;
      text-align: right;
      font-size: 12px;
      color: #868e96;
      margin-top: 4px;
    }

    .hint {
      display: block;
      font-size: 12px;
      color: #868e96;
      margin-top: 4px;
    }
  }

  .social-inputs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;

    .social-input-row {
      display: flex;
      align-items: center;
      gap: 8px;

      .social-icon {
        font-size: 20px;
        min-width: 32px;
        text-align: center;
      }

      input {
        flex: 1;
      }

      .remove-social {
        background: none;
        border: none;
        font-size: 20px;
        color: #dc3545;
        cursor: pointer;
        padding: 0 4px;
      }
    }
  }

  .add-social {
    display: flex;
    gap: 8px;

    select {
      flex: 1;
      padding: 8px 12px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 14px;
    }

    .add-btn {
      padding: 8px 16px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;

      &:hover:not(:disabled) {
        background: darken($primary-orange, 10%);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    .btn-secondary {
      padding: 10px 24px;
      background: #e9ecef;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      color: #495057;
      cursor: pointer;

      &:hover {
        background: #dee2e6;
      }
    }

    .btn-primary {
      padding: 10px 24px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        background: darken($primary-orange, 10%);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}
</style>
